import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import './Breadcrumb.css';

const routeConfig = {
  '/matrimony/search': { label: 'Search Profiles', parent: '/matrimony' },
  '/matrimony/dashboard': { label: 'User Dashboard', parent: '/matrimony' },
  '/matrimony/matches': { label: 'My Matches', parent: '/matrimony/dashboard' },
  '/matrimony/interests': { label: 'Interest Requests', parent: '/matrimony/dashboard' },
  '/matrimony/profile/me': { label: 'My Profile', parent: '/matrimony/dashboard' },
  '/matrimony/profile/edit': { label: 'Edit Profile Details', parent: '/matrimony/profile/me' },
  '/matrimony/complete-profile': { label: 'Profile Setup Wizard', parent: '/matrimony/dashboard' },
  '/matrimony/plans': { label: 'Membership Plans', parent: '/matrimony' },
  '/matrimony/plans/upgrade': { label: 'Upgrade Subscription', parent: '/matrimony/plans' },
  '/matrimony/settings': { label: 'Account Settings', parent: '/matrimony/dashboard' },
  '/matrimony/verification-pending': { label: 'Verification Status', parent: '/matrimony/dashboard' },
  '/matrimony/login': { label: 'Member Login', parent: '/matrimony' },
  '/matrimony/signup': { label: 'Register Account', parent: '/matrimony' },
  '/matrimony/forgot-password': { label: 'Forgot Password', parent: '/matrimony/login' },
  '/matrimony/reset-password': { label: 'Reset Password', parent: '/matrimony/login' },
  '/privacy': { label: 'Privacy Policy', parent: '/terms' },
  '/privacy-policy': { label: 'Privacy Policy', parent: '/terms' },
  '/terms': { label: 'Terms of Use', parent: '/' },
  '/terms-of-service': { label: 'Terms of Use', parent: '/' },
  '/terms-of-use': { label: 'Terms of Use', parent: '/' },
  '/guidelines': { label: 'Community Guidelines', parent: '/terms' },
  '/community-guidelines': { label: 'Community Guidelines', parent: '/terms' },
};

const getBreadcrumbItems = (pathname) => {
  if (!pathname || pathname === '/' || pathname === '/community' || pathname === '/matrimony') {
    return null;
  }

  // Handle dynamic candidate profile details: /matrimony/profile/:identifier
  if (
    (pathname.startsWith('/matrimony/profile/') || pathname.startsWith('/profile/')) &&
    pathname !== '/matrimony/profile/me' &&
    pathname !== '/matrimony/profile/edit' &&
    pathname !== '/profile/me' &&
    pathname !== '/profile/edit'
  ) {
    return [
      { label: 'Home', path: '/matrimony' },
      { label: 'Search Profiles', path: '/matrimony/search' },
      { label: 'Candidate Profile Details', path: pathname }
    ];
  }

  const items = [{ label: 'Home', path: '/matrimony' }];

  if (routeConfig[pathname]) {
    const config = routeConfig[pathname];
    if (config.parent && config.parent !== '/') {
      const parentConfig = routeConfig[config.parent];
      if (parentConfig) {
        items.push({ label: parentConfig.label, path: config.parent });
      }
    }
    items.push({ label: config.label, path: pathname });
  } else {
    // Dynamic fallback for any unmatched inner routes
    const segments = pathname.split('/').filter(Boolean);
    let currentPath = '';
    segments.forEach((seg) => {
      currentPath += `/${seg}`;
      const formatted = seg.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
      items.push({ label: formatted, path: currentPath });
    });
  }

  return items;
};

const Breadcrumb = () => {
  const location = useLocation();
  const items = getBreadcrumbItems(location.pathname);

  if (!items || items.length <= 1) {
    return null;
  }

  return (
    <nav
      aria-label="Breadcrumb"
      className="mb-breadcrumb-nav w-full"
    >
      <div className="max-w-[1360px] mx-auto px-3 sm:px-6">
        <ol
          className="mb-breadcrumb-ol"
          itemScope
          itemType="https://schema.org/BreadcrumbList"
        >
          {items.map((item, index) => {
            const isLast = index === items.length - 1;

            return (
              <li
                key={item.path + index}
                className="mb-breadcrumb-li"
                itemProp="itemListElement"
                itemScope
                itemType="https://schema.org/ListItem"
              >
                {index > 0 && (
                  <span className="mb-breadcrumb-sep" aria-hidden="true">
                    <svg className="w-3 h-3 text-slate-400 stroke-current fill-none" viewBox="0 0 24 24">
                      <path d="M9 5l7 7-7 7" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                )}

                {isLast ? (
                  <span
                    className="mb-breadcrumb-active"
                    aria-current="page"
                    itemProp="name"
                  >
                    {index === 0 && (
                      <svg className="w-3.5 h-3.5 mr-1 flex-shrink-0" viewBox="0 0 20 20">
                        <defs>
                          <linearGradient id="bcHomeGradActive" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#8A216B" />
                            <stop offset="100%" stopColor="#F03F16" />
                          </linearGradient>
                        </defs>
                        <path fill="url(#bcHomeGradActive)" d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
                      </svg>
                    )}
                    {item.label}
                  </span>
                ) : (
                  <Link
                    to={item.path}
                    className="mb-breadcrumb-link group"
                    itemProp="item"
                  >
                    {index === 0 && (
                      <svg className="w-3.5 h-3.5 mr-1 flex-shrink-0 transition-transform group-hover:scale-110" viewBox="0 0 20 20">
                        <defs>
                          <linearGradient id="bcHomeGradLink" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#8A216B" />
                            <stop offset="100%" stopColor="#F03F16" />
                          </linearGradient>
                        </defs>
                        <path fill="url(#bcHomeGradLink)" d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
                      </svg>
                    )}
                    <span itemProp="name">{item.label}</span>
                  </Link>
                )}

                <meta itemProp="position" content={String(index + 1)} />
              </li>
            );
          })}
        </ol>
      </div>
    </nav>
  );
};

export default Breadcrumb;
